import { PLAN_TIERS, planRank, type Plan } from "@/lib/plan";

export const PLAN_MANAGEMENT_PATH =
  "/settings?tab=billing&checkout=manage-existing-plan";

export type SelfServeCheckoutSubscriptionMirror = {
  stripeSubscriptionId: string | null | undefined;
  stripeSubscriptionStatus: string | null | undefined;
};

export type SelfServeCheckoutAccount = SelfServeCheckoutSubscriptionMirror & {
  plan: string | null | undefined;
};

const TERMINAL_SUBSCRIPTION_STATUSES = new Set([
  "canceled",
  "incomplete_expired",
]);

function knownPlan(value: string | null | undefined): Plan {
  return (PLAN_TIERS as readonly string[]).includes(value ?? "")
    ? (value as Plan)
    : "starter";
}

/**
 * Stripe Checkout is an upgrade boundary, not a subscription-management UI.
 * Any owner with a non-terminal Stripe subscription must manage every plan
 * change through Billing. Rank checks alone are insufficient: an active Pro
 * subscriber buying Business through a fresh Checkout would create a second
 * subscription. Check both User and Account because webhook mirror repair is
 * deliberately asynchronous after a persistent Account-sync failure. Owners
 * with no subscription (including internal no-card trials) may still start an
 * upward checkout. A subscription-free or terminal Pro account may also buy
 * Pro at the same rank so the setup-service trial can convert and a cancelled
 * customer can resubscribe. Equal-rank Business/Site Control remains managed:
 * those tiers can be manually invoiced even when Stripe mirrors are empty.
 */
export function canStartSelfServePlanCheckout(
  account: SelfServeCheckoutAccount,
  ownerUser: SelfServeCheckoutSubscriptionMirror,
  targetPlan: Plan,
): boolean {
  const currentRank = planRank(knownPlan(account.plan));
  const targetRank = planRank(targetPlan);

  if ([account, ownerUser].some(hasNonTerminalSubscription)) return false;
  if (targetRank > currentRank) return true;

  const proRank = planRank("pro_monthly");
  return currentRank === proRank && targetRank === proRank;
}

function hasNonTerminalSubscription(
  mirror: SelfServeCheckoutSubscriptionMirror,
): boolean {
  const status = mirror.stripeSubscriptionStatus?.toLowerCase() ?? null;
  const hasSubscriptionEvidence =
    Boolean(mirror.stripeSubscriptionId) ||
    (mirror.stripeSubscriptionStatus !== null &&
      mirror.stripeSubscriptionStatus !== undefined);
  if (!hasSubscriptionEvidence) return false;

  return status === null || !TERMINAL_SUBSCRIPTION_STATUSES.has(status);
}
